import React, { useEffect, useState } from 'react';
import { Separator } from '../ui/separator';
import { DatePickerWithRange } from '../ui/date-picker-range';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ROLES } from '../../constants';
import { Badge } from '../ui/badge';
import { useSelector } from 'react-redux';
import { getMonths } from '../../modules/analytics/anaytics.model';
import { useDispatch } from 'react-redux';
import { doGetCompanies } from '../../modules/companies/stores/thunk';
import usePageView from '../../hooks/usePageView';
import { selectAllCompanies } from '../../modules/companies/stores/slice';
import { Bike, Calendar, Store } from 'lucide-react';
import { AppDispatch } from '../../redux/store';
import { doGetSustainabilityData } from '../../modules/sustainability/stores/thunk';
import { selectSustainabilityData, SustainabilityData } from '../../modules/sustainability/stores/slice';
import { OrderTotalCard } from '../Elements/OrderTotalCard';
import { SimplifiedStatsCard } from '../Elements/SimplifiedStatsCard';
import { EmissionSaving } from '../Elements/EmissionSaving';

const SustainabilityPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const role = useSelector((state: { auth: any }) => state.auth.user.role);
  const companies = useSelector((state) => selectAllCompanies(state));
  // const { isFetching } = useSelector((state: RootState) => state.sustainability);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectKey, setSelectKey] = useState(+new Date());
  const months = getMonths();
  const [customRange, setCustomRange] = useState(false);
  const [month, setMonth] = useState(months[0].value);
  const [range, setRange] = useState(months[0].range);

  useEffect(() => {
    dispatch(doGetCompanies({ page: 1, take: 1000 }));
  }, []);

  useEffect(() => {
    if (month === null) {
      return;
    }
    setRange(months.find((m) => m.value === month)?.range);
  }, [month]);

  // Emit page view event
  usePageView(window.location.href);

  const sustainabilityData: SustainabilityData = useSelector((state) => selectSustainabilityData(state));

  useEffect(() => {
    const query = {};
    if (range !== null) {
      query['startRange'] = range[0].toISOString();
      query['endRange'] = range[1].toISOString();
    }

    if (selectedCompany) {
      query['companyId'] = selectedCompany;
    }

    dispatch(
      doGetSustainabilityData({
        ...query,
      }),
    );
  }, [range, selectedCompany, dispatch]);

  // const isEmpty = useMemo(() => {
  //   return sustainabilityData === null;
  // }, [sustainabilityData]);

  useEffect(() => {
    console.log(range);
  }, [range]);

  useEffect(() => {
    console.log(sustainabilityData);
  }, [sustainabilityData]);

  return (
    <div className="bg-white min-h-screen">
      <div className="flex justify-center px-4 py-10">
        <div className="w-full max-w-screen-xl bg-white">
          {/* Top Bar */}
          <div className="flex justify-between items-center">
            <div className="text-left">
              <div className="flex">
                <h1 className="text-2xl font-semibold">Sustainability</h1>
                <Badge variant="highlight" className="ml-4 px-4 my-1">
                  Beta
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">Let&apos;s see how we&apos;ve been doing lately</p>
            </div>
            <div>
              <div className="flex justify-end ">
                {role === ROLES.ADMIN && (
                  <Select onValueChange={(newValue) => setSelectedCompany(newValue)}>
                    <SelectTrigger className="w-[240px] mr-4">
                      <Store className="h-4 w-4" />
                      <SelectValue placeholder="Select a company" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        {companies.map((c) => (
                          <SelectItem key={c.id} value={c.companyId}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                )}
                <Select
                  key={selectKey}
                  onValueChange={(newValue) => {
                    setCustomRange(false);
                    setMonth(Number(newValue));
                  }}
                >
                  <SelectTrigger className="w-[180px] flex items-center">
                    <Calendar className="h-4 w-4" />
                    <SelectValue placeholder={customRange ? 'custom range' : 'This month'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {months.map((m) => (
                        <SelectItem key={m.value} value={m.value.toString()}>
                          {m.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <DatePickerWithRange
                  className="ml-2"
                  onChange={(d) => {
                    setCustomRange(true);
                    setSelectKey(+new Date());
                    setRange([d.from, d.to]);
                    // setMonth(null);
                  }}
                  preset={{ from: range[0], to: range[1] }}
                />
              </div>
            </div>
          </div>
          <Separator className="mt-4" />

          <div className="mt-6">
            <div>
              <div className="flex items-center gap-2">
                <Bike className="h-6 w-6 mb-4" />
                <h3 className="text-xl font-semibold mb-4">Last Mile</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                {/* Column 1, Row 1 */}
                <div className="md:row-span-1">
                  <OrderTotalCard
                    title="Total Attempts"
                    subtitle="Delivery attempts"
                    numberOfOrders={sustainabilityData?.lastMile.attempts}
                  />
                </div>
                {/* Column 2, spanning Rows 1 & 2 */}
                <div className="md:row-span-2">
                  <SimplifiedStatsCard title="Transport Mode" stats={sustainabilityData?.lastMile.transport} />
                </div>
                {/* Column 3, Row 1 */}
                <div className="md:row-span-1">
                  <OrderTotalCard
                    title="Total Emissions"
                    subtitle="Total emissions for all deliveries"
                    numberOfOrders={sustainabilityData?.lastMile.totalEmissions || 0}
                    suffix="kg CO2"
                  />
                </div>
                {/* Column 4, spanning Rows 1 & 2 */}
                <div className="md:row-span-2">
                  <EmissionSaving
                    title="Emission Saving"
                    subtitle="Compared to Diesel benchmark"
                    totalEmission={sustainabilityData?.lastMile.totalEmissions}
                    benchmarkEmission={sustainabilityData?.lastMile.benchmarkTotalEmissions}
                  />
                </div>
                {/* Column 1, Row 2 */}
                <div className="md:row-span-1">
                  <OrderTotalCard
                    title="Emission Per Order"
                    subtitle="Average"
                    numberOfOrders={sustainabilityData?.lastMile.emissionPerOrder || 0}
                    suffix="g CO2"
                  />
                </div>
                {/* Column 3, Row 2 */}
                <div className="md:row-span-1">
                  <OrderTotalCard
                    title="Average Distance"
                    subtitle="From hub to delivery"
                    numberOfOrders={sustainabilityData?.lastMile.averageDistance || 0}
                    suffix="km"
                  />
                </div>
              </div>
            </div>

            {/* Add margin-top (mt-6) for spacing */}
            {/* <div className="mt-6">
              <div className="flex items-center gap-2">
                <Truck className="h-6 w-6 mb-4" />
                <h3 className="text-xl font-semibold mb-4">First Mile</h3>
              </div>
              <p className="italic text-gray-400">Coming soon...</p>
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SustainabilityPage;
